"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { Sparkles, X } from "lucide-react";
import { useT } from "@/lib/use-i18n";
import { cn } from "@/lib/utils";
import type { CareTool } from "@/lib/care-prompt-keys";
export type FirstSuccessPromptProps = {
  tool: CareTool;
  onDismiss: () => void;
  onCta: () => void;
  className?: string;
  /** Salida animada (la provee CareDockHost vía useAnimatedOpen). */
  leaving?: boolean;
};

/**
 * One-time warm prompt after the first successful chat session.
 * The CTA returns the user to the main Vortixy workspace.
 */
export function FirstSuccessPrompt(t0) {
  const $ = _c(40);
  const {
    onDismiss,
    onCta,
    className,
    leaving: t1
  } = t0;
  const leaving = t1 === undefined ? false : t1;
  const t = useT();
  let t2;
  if ($[0] !== t) {
    t2 = t("firstSuccessCta");
    $[0] = t;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const ctaLabel = t2;
  let t3;
  if ($[2] !== className) {
    t3 = cn("pointer-events-none fixed inset-x-0 bottom-0 z-[80] flex justify-center p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:p-4", className);
    $[2] = className;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = leaving ? "animate-out fade-out slide-out-to-bottom-1 duration-150 motion-reduce:animate-none" : "animate-in fade-in slide-in-from-bottom-1 duration-150 motion-reduce:animate-none";
  let t5;
  if ($[4] !== t4) {
    t5 = cn("care-dock-panel pointer-events-auto relative w-full max-w-[28rem] overflow-hidden", "rounded-[var(--radius-card)] border border-[var(--border-strong)]", "bg-[rgba(14,14,20,0.72)] shadow-[var(--shadow-raised)]", "backdrop-blur-sm backdrop-saturate-150", "supports-[backdrop-filter]:bg-[rgba(14,14,20,0.55)]", t4);
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/35 to-transparent" aria-hidden="true" />;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="flex size-9 shrink-0 items-center justify-center rounded-xl border border-[var(--border-strong)] bg-[var(--accent-soft)]" aria-hidden="true"><Sparkles className="size-4 text-[var(--accent)]" /></div>;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== t) {
    t8 = t("firstSuccessTitle");
    $[8] = t;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== t8) {
    t9 = <p id="first-success-title" className="text-[var(--step--1)] font-semibold leading-snug text-foreground">{t8}</p>;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== t) {
    t10 = t("firstSuccessBody");
    $[12] = t;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== t10) {
    t11 = <p className="mt-0.5 text-[var(--step--2)] leading-relaxed text-muted-foreground">{t10}</p>;
    $[14] = t10;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = cn("inline-flex min-h-9 items-center justify-center rounded-full", "bg-[var(--accent)] px-3.5 text-[var(--step--2)] font-semibold text-[var(--accent-foreground)]", "transition-[transform,opacity] duration-200 ease-[var(--ease-enter)]", "hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]/40", "active:scale-[0.98]");
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== ctaLabel || $[18] !== onCta) {
    t13 = <div className="mt-3 flex flex-wrap gap-2"><Link href="/chat" data-testid="first-success-cta" onClick={onCta} className={t12}>{ctaLabel}</Link></div>;
    $[17] = ctaLabel;
    $[18] = onCta;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== t11 || $[21] !== t13 || $[22] !== t9) {
    t14 = <div className="min-w-0 flex-1">{t9}{t11}{t13}</div>;
    $[20] = t11;
    $[21] = t13;
    $[22] = t9;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = cn("inline-flex size-9 shrink-0 items-center justify-center rounded-full", "text-muted-foreground transition-colors duration-150", "hover:bg-[var(--muted)] hover:text-foreground", "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]/35");
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t) {
    t16 = t("firstSuccessClose");
    $[25] = t;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <X className="size-3.5" aria-hidden="true" />;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== onDismiss || $[29] !== t16) {
    t18 = <button type="button" data-testid="first-success-close" onClick={onDismiss} className={t15} aria-label={t16}>{t17}</button>;
    $[28] = onDismiss;
    $[29] = t16;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== t14 || $[32] !== t18) {
    t19 = <div className="relative flex items-start gap-3 p-3.5 sm:p-4">{t7}{t14}{t18}</div>;
    $[31] = t14;
    $[32] = t18;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== t19 || $[35] !== t5) {
    t20 = <div role="region" aria-labelledby="first-success-title" data-testid="first-success-prompt" className={t5}>{t6}{t19}</div>;
    $[34] = t19;
    $[35] = t5;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== t20 || $[38] !== t3) {
    t21 = <div className={t3} data-testid="first-success-root">{t20}</div>;
    $[37] = t20;
    $[38] = t3;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  return t21;
}